// Types and interfaces for distribution components

export interface PackageData {
  type: 'package' | 'standalone' | 'custom';
  tier?: string; // '1', '2', '3', '4-5'
  customTiers?: string[]; // For custom package
  customPRs?: number; // For custom package
  tierPRCounts?: { [tier: string]: number }; // For custom package: breakdown of PRs per tier
  price: number;
  credits: number; // PRs included in package
}

export interface SelectionData {
  geography: string[];
  industry: string[];
  subClusters: string[];
  tier: string[];
  packageType: 'standalone' | 'package';
  quantity: number;
  priorityPublications?: string[];
}

export const TIER_PRICING = {
  standalone: {
    '1': { name: 'Enterprise', price: 3999, tiers: ['1'] },
    '2': { name: 'Premium', price: 2999, tiers: ['2'] },
    '3': { name: 'Pro', price: 1999, tiers: ['3'] },
    '4-5': { name: 'Basic', price: 999, tiers: ['4', '5'] }
  },
  package: {
    '1': { name: 'Enterprise', price: 69999, prs: 20, tiers: ['1'], credits: 20 },
    '2': { name: 'Premium', price: 52999, prs: 20, tiers: ['2'], credits: 20 },
    '3': { name: 'Pro', price: 34999, prs: 20, tiers: ['3'], credits: 20 },
    '4-5': { name: 'Basic', price: 17999, prs: 20, tiers: ['4', '5'], credits: 20 }
  }
};

export const getTierKey = (tiers: string[]): string => {
  if (tiers.includes('1')) return '1';
  if (tiers.includes('2')) return '2';
  if (tiers.includes('3')) return '3';
  if (tiers.includes('4') || tiers.includes('5')) return '4-5';
  return '4-5';
};

export const calculateItemPrice = (
  type: 'standalone' | 'package',
  tiers: string[],
  quantity: number
): { pricePerUnit: number; totalPrice: number; tierName: string; tierKey: string; expiryDays: number; tierBreakdown: Array<{tier: string, price: number, tierName?: string}> } => {
  if (tiers.length === 0) {
    return {
      pricePerUnit: 0,
      totalPrice: 0,
      tierName: 'No Tier Selected',
      tierKey: '',
      expiryDays: type === 'standalone' ? 30 : 365,
      tierBreakdown: []
    };
  }

  // Calculate price per tier and sum them up
  const tierBreakdown: Array<{tier: string, price: number, tierName?: string}> = [];
  let totalPricePerUnit = 0;

  tiers.forEach(tier => {
    // Handle Corp Crunch specially
    if (tier === 'corp-crunch') {
      const corpCrunchPrice = 1499;
      totalPricePerUnit += corpCrunchPrice;
      tierBreakdown.push({ tier: 'corp-crunch', price: corpCrunchPrice, tierName: 'Corp Crunch' });
      return;
    }

    let tierKey: string;
    if (tier === '1') tierKey = '1';
    else if (tier === '2') tierKey = '2';
    else if (tier === '3') tierKey = '3';
    else tierKey = '4-5';

    const pricing = TIER_PRICING[type][tierKey as keyof typeof TIER_PRICING.standalone];
    const tierPrice = pricing.price;
    totalPricePerUnit += tierPrice;
    tierBreakdown.push({ tier, price: tierPrice });
  });

  const totalPrice = totalPricePerUnit * quantity;
  const expiryDays = type === 'standalone' ? 30 : 365;
  
  // Handle tier name for Corp Crunch
  let tierName: string;
  if (tiers.includes('corp-crunch') && tiers.length === 1) {
    tierName = 'Corp Crunch';
  } else if (tiers.length === 1) {
    tierName = TIER_PRICING[type][getTierKey(tiers) as keyof typeof TIER_PRICING.standalone].name;
  } else {
    tierName = `${tiers.length} Tiers Selected`;
  }

  return {
    pricePerUnit: totalPricePerUnit,
    totalPrice,
    tierName,
    tierKey: getTierKey(tiers),
    expiryDays,
    tierBreakdown
  };
};

